import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  selectEnvironmentInteractionPoints,
  TakePosition,
} from "../../app/slices/EnvironmentSlice";
import {
  selectSequencePositionPicker,
  selectShowEnvironmentProps,
} from "../../app/slices/GlobalSlice";
import {
  selectIsCameraSelected,
  selectSelectedTake3D,
} from "../../app/slices/TrainingSlice";
import "./Viewport3DEnvironmentMetadata.css";
import Viewport3DNpcLayer from "./Viewport3DNpcLayer";
import Viewport3DTakeLocationLayer from "./ViewportTakeLocationLayer";
import Viewport3DPropLayer from "./Viewport3DPropLayer";
import Viewport3DCameraLayer from "./Viewport3DCameraLayer";
import Viewport3DSituationLayer from "./situation_layer/Viewport3DSituationLayer";
import Viewport3DSequenceActorPositionLayer from "./Viewport3DSequenceActorPositionLayer";
import Viewport3DSequenceCameraPositionLayer from "./Viewport3DSequenceCameraPositionLayer";
import Viewport3DSequenceProximityPositionLayer from "./Viewport3DSequenceProximityPositionLayer";
import Viewport3DSequencePropPositionLayer from "./Viewport3DSequencePropPositionLayer";
import Viewport3DSequenceLookatTargetLayer from "./Viewport3DSequenceLookatTargetLayer";
import Viewport3DEnvironmentPropLayer from "./Viewport3DEnvironmentPropLayer";

export interface Viewport3DLayerProps {
  currentTakeLocation?: TakePosition;
  takePositions: TakePosition[];
  isCameraSelected?: boolean;
}

export default function EnvironmentMetadataView(props: {
  visibleLayerIds?: string[];
}) {
  const { visibleLayerIds = [] } = { ...props };
  const take = useSelector(selectSelectedTake3D);
  const takePositions = useSelector(selectEnvironmentInteractionPoints);
  const isTakeCameraSelected = useSelector(selectIsCameraSelected);
  const sequencePositionPicker = useSelector(selectSequencePositionPicker);
  const showEnvironmentProps = useSelector(selectShowEnvironmentProps);

  const currentTakeLocationAll = useMemo(() => {
    return take?.interactionPointId
      ? takePositions.find((point) => point.id === take.interactionPointId)
      : undefined;
  }, [take, takePositions]);

  const effectiveVisibleLayerIds = useMemo(() => {
    if (!currentTakeLocationAll?.layer) return visibleLayerIds;
    if (visibleLayerIds.includes(currentTakeLocationAll.layer))
      return visibleLayerIds;
    return [...visibleLayerIds, currentTakeLocationAll.layer];
  }, [currentTakeLocationAll?.layer, visibleLayerIds]);

  const filteredTakePositions = useMemo(() => {
    if (effectiveVisibleLayerIds.length === 0) return takePositions;

    return takePositions.filter((point) => {
      if (!point.layer) return effectiveVisibleLayerIds.includes("default");
      return effectiveVisibleLayerIds.includes(point.layer);
    });
  }, [effectiveVisibleLayerIds, takePositions]);

  const currentTakeLocation = useMemo(() => {
    return take?.interactionPointId
      ? filteredTakePositions.find((point) => point.id === take.interactionPointId)
      : undefined;
  }, [filteredTakePositions, take]);

  const isSequencePositionPicking =
    sequencePositionPicker.isOpen;

  if (isSequencePositionPicking) {
    if (sequencePositionPicker.positionType === "takeLocation") {
      return (
        <Viewport3DTakeLocationLayer
          currentTakeLocation={currentTakeLocation}
          takePositions={filteredTakePositions}
          isCameraSelected={isTakeCameraSelected}
          pickingMode
        />
      );
    }
    if (sequencePositionPicker.positionType === "lookatTarget") {
      return <Viewport3DSequenceLookatTargetLayer />;
    }
    if (sequencePositionPicker.positionType === "camera") {
      return <Viewport3DSequenceCameraPositionLayer />;
    }
    if (sequencePositionPicker.positionType === "prop") {
      return <Viewport3DSequencePropPositionLayer />;
    }
    if (sequencePositionPicker.positionType === "proximity") {
      return <Viewport3DSequenceProximityPositionLayer />;
    }
    return <Viewport3DSequenceActorPositionLayer />;
  }

  if (showEnvironmentProps) {
    return <Viewport3DEnvironmentPropLayer />;
  }

  return (
    <>
      <Viewport3DNpcLayer
        currentTakeLocation={currentTakeLocation}
        takePositions={filteredTakePositions}
        isCameraSelected={isTakeCameraSelected}
      />
      <Viewport3DTakeLocationLayer
        currentTakeLocation={currentTakeLocation}
        takePositions={filteredTakePositions}
        isCameraSelected={isTakeCameraSelected}
      />
      <Viewport3DPropLayer
        currentTakeLocation={currentTakeLocation}
        takePositions={filteredTakePositions}
        isCameraSelected={isTakeCameraSelected}
      />
      <Viewport3DCameraLayer
        currentTakeLocation={currentTakeLocation}
        takePositions={filteredTakePositions}
        isCameraSelected={isTakeCameraSelected}
      />
      <Viewport3DSituationLayer
        currentTakeLocation={currentTakeLocation}
        takePositions={filteredTakePositions}
        isCameraSelected={isTakeCameraSelected}
      />
      <Viewport3DEnvironmentPropLayer />
      <Viewport3DSequenceActorPositionLayer />
    </>
  );
}
